/**
 * TasksPage — the cross-property action backlog (design handoff "Stayframe - Tasks").
 *
 * Memory banner + To do / Snoozed / Done stat cards, a property filter, and the
 * three live sections. Checkbox → done, snooze → resume, undo, urgent-first,
 * empty states. Backed by useTasks (localStorage store; see its header for the
 * backend follow-up). CTAs deep-link into the current property's surface.
 */

import { useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { Clock, Check, ListChecks } from "lucide-react"
import { cn } from "@/lib/utils"
import { useTasks, type Task } from "./useTasks"

const SRC_COLOR: Record<string, string> = {
  pricing: "text-rose-strong",
  reputation: "text-amber-strong",
  listing: "text-sky",
  guest: "text-brand-strong",
  rules: "text-muted-foreground",
  billing: "text-ink-2",
}
const SRC_DOT: Record<string, string> = {
  pricing: "bg-rose",
  reputation: "bg-amber",
  listing: "bg-sky",
  guest: "bg-brand",
  rules: "bg-muted-foreground",
  billing: "bg-ink-2",
}

/** Stable per-property colour derived from the property id (Tailwind class + CSS color for charts). */
const PROP_PALETTE = ["bg-brand", "bg-clay", "bg-sky", "bg-amber", "bg-rose"]
const PROP_CHART_COLORS = ["var(--sf-brand)", "var(--sf-clay)", "var(--sf-sky)", "var(--sf-amber)", "var(--sf-rose)"]
function propHash(propertyId: string): number {
  let h = 0
  for (let i = 0; i < propertyId.length; i++) h = (h + propertyId.charCodeAt(i)) % 997
  return h
}
export function propDotClass(propertyId: string): string {
  return PROP_PALETTE[propHash(propertyId) % PROP_PALETTE.length]
}
export function propChartColor(propertyId: string): string {
  return PROP_CHART_COLORS[propHash(propertyId) % PROP_CHART_COLORS.length]
}

export function TasksPage() {
  const navigate = useNavigate()
  const { tasks, counts, markDone, snooze, resume } = useTasks()
  const [filter, setFilter] = useState<string>("all")

  // Property filter chips derived from the tasks present (real property ids).
  const properties = useMemo(() => {
    const seen = new Map<string, string>()
    for (const t of tasks) if (!seen.has(t.propertyId)) seen.set(t.propertyId, t.propName)
    return Array.from(seen, ([key, name]) => ({ key, name }))
  }, [tasks])

  const inFilter = (t: Task) => filter === "all" || t.propertyId === filter
  const todo = tasks.filter((t) => t.status === "todo" && inFilter(t)).sort((a, b) => (b.urgent ? 1 : 0) - (a.urgent ? 1 : 0))
  const snoozed = tasks.filter((t) => t.status === "snoozed" && inFilter(t))
  const done = tasks.filter((t) => t.status === "done" && inFilter(t))

  // CTAs open the surface inside the TASK's own property, not the current one.
  const openTask = (t: Task) => {
    if (!t.propertyId) return
    navigate(`/properties/${t.propertyId}/${t.ctaSurface}`)
  }

  const filters = [{ key: "all", name: "All properties" }, ...properties]

  if (tasks.length === 0) {
    return (
      <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-6 md:px-7">
        <section className="mb-[18px] flex items-center gap-3.5 rounded-card border border-brand/25 bg-brand-soft px-[18px] py-4">
          <span className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[11px] bg-white">
            <Clock className="h-[19px] w-[19px] text-brand-strong" strokeWidth={1.8} />
          </span>
          <div>
            <div className="text-[13.5px] font-semibold text-brand-strong" style={{ fontWeight: 650 }}>Your action backlog, with memory</div>
            <div className="mt-0.5 text-[12.5px] leading-[1.45] text-ink-2">
              Actions you create from reviews, pricing and guest-experience insights collect here — and once handled, they stop resurfacing.
            </div>
          </div>
        </section>
        <div className="flex flex-col items-center justify-center rounded-card border border-dashed border-line-2 bg-card px-6 py-16 text-center">
          <ListChecks className="h-8 w-8 text-muted-foreground" strokeWidth={1.6} />
          <p className="mt-3 text-[14px] font-semibold text-foreground">No tasks yet</p>
          <p className="mt-1 max-w-sm text-[12.5px] text-muted-foreground">
            Nothing needs you right now. Tasks appear here when you create them from insights across your properties.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-6 md:px-7">
      {/* MEMORY BANNER + STATS */}
      <section className="mb-[18px] grid grid-cols-1 gap-3.5 lg:grid-cols-[1.5fr_1fr]">
        <div className="flex items-center gap-3.5 rounded-card border border-brand/25 bg-brand-soft px-[18px] py-4">
          <span className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[11px] bg-white">
            <Clock className="h-[19px] w-[19px] text-brand-strong" strokeWidth={1.8} />
          </span>
          <div>
            <div className="text-[13.5px] font-semibold text-brand-strong" style={{ fontWeight: 650 }}>Stayframe remembers what you’ve handled</div>
            <div className="mt-0.5 text-[12.5px] leading-[1.45] text-ink-2">
              Completed and snoozed items stop resurfacing in your daily triage — so the same advice never nags twice.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {[
            { label: "To do", value: counts.todo, color: "text-foreground" },
            { label: "Snoozed", value: counts.snoozed, color: "text-muted-foreground" },
            { label: "Done", value: counts.done, color: "text-brand-strong" },
          ].map((s) => (
            <div key={s.label} className="rounded-[14px] border border-border bg-card px-3 py-3.5 text-center shadow-sm">
              <div className={cn("font-display text-[24px] font-bold", s.color)}>{s.value}</div>
              <div className="mt-0.5 text-[10.5px] font-semibold text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FILTER */}
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <span className="text-[11.5px] font-semibold text-muted-foreground">Filter</span>
        {filters.map((f) => {
          const active = filter === f.key
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-all",
                active ? "border-foreground bg-foreground text-white" : "border-border bg-card text-ink-2 hover:border-line-2",
              )}
            >
              {f.key !== "all" && <span className={cn("h-2 w-2 rounded-[3px]", propDotClass(f.key))} />}
              {f.name}
            </button>
          )
        })}
      </div>

      {/* TO DO */}
      <div className="mb-[11px] text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">To do · {todo.length}</div>
      <div className="mb-[26px] flex flex-col gap-2.5">
        {todo.length === 0 ? (
          <div className="flex items-center gap-3 rounded-[14px] border border-dashed border-line-2 p-5 text-ink-2">
            <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px] bg-brand-soft">
              <Check className="h-[18px] w-[18px] text-brand" strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-[14px] font-semibold text-foreground" style={{ fontWeight: 650 }}>Nothing open here.</p>
              <span className="text-[12.5px] text-muted-foreground">You’re all caught up for this view.</span>
            </div>
          </div>
        ) : (
          todo.map((t) => (
            <article
              key={t.id}
              className={cn(
                "flex items-start gap-3.5 rounded-[14px] border bg-card p-4 shadow-sm transition-colors hover:border-line-2",
                t.urgent ? "border-rose/35" : "border-border",
              )}
            >
              <button
                type="button"
                onClick={() => markDone(t.id)}
                title="Mark done"
                className="mt-px h-[23px] w-[23px] flex-none rounded-[7px] border-2 border-line-2 bg-card transition-all hover:border-brand hover:bg-brand-soft"
              />
              <div className="min-w-0 flex-1">
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <span className={cn("inline-flex items-center gap-1.5 text-[11px] font-semibold", SRC_COLOR[t.srcKey] || "text-muted-foreground")}>
                    <span className={cn("h-1.5 w-1.5 rounded-full", SRC_DOT[t.srcKey] || "bg-muted-foreground")} />
                    {t.source}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-ink-2">
                    <span className={cn("h-2 w-2 rounded-[3px]", propDotClass(t.propertyId))} />
                    {t.propName}
                  </span>
                  {t.urgent && (
                    <span className="rounded-full bg-rose-soft px-[7px] py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.05em] text-rose-strong">Urgent</span>
                  )}
                </div>
                <h4 className="text-[14.5px] font-semibold text-foreground" style={{ fontWeight: 650 }}>{t.title}</h4>
                <p className="mt-0.5 text-[12.5px] leading-[1.5] text-ink-2">{t.detail}</p>
              </div>
              <div className="flex flex-none items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => openTask(t)}
                  className="whitespace-nowrap rounded-[9px] border border-border bg-card px-3 py-2 text-[12px] font-semibold text-foreground transition-all hover:border-foreground hover:bg-foreground hover:text-white"
                >
                  {t.cta}
                </button>
                <button
                  type="button"
                  onClick={() => snooze(t.id)}
                  title="Snooze"
                  className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-border bg-card text-muted-foreground transition-all hover:bg-line-soft hover:text-foreground"
                >
                  <Clock className="h-[15px] w-[15px]" strokeWidth={1.9} />
                </button>
              </div>
            </article>
          ))
        )}
      </div>

      {/* SNOOZED */}
      {snoozed.length > 0 && (
        <>
          <div className="mb-[11px] text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">Snoozed · {snoozed.length}</div>
          <div className="mb-[26px] flex flex-col gap-2.5">
            {snoozed.map((t) => (
              <article key={t.id} className="flex items-center gap-3.5 rounded-[14px] border border-border bg-line-soft px-4 py-3.5">
                <Clock className="h-[18px] w-[18px] flex-none text-muted-foreground" strokeWidth={1.9} />
                <div className="min-w-0 flex-1">
                  <h4 className="text-[13.5px] font-semibold text-ink-2">{t.title}</h4>
                  <div className="mt-0.5 text-[11px] font-medium text-muted-foreground">{t.propName} · snoozed</div>
                </div>
                <button
                  type="button"
                  onClick={() => resume(t.id)}
                  className="flex-none rounded-[9px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-muted-foreground transition-all hover:border-brand hover:text-brand-strong"
                >
                  Resume
                </button>
              </article>
            ))}
          </div>
        </>
      )}

      {/* DONE */}
      {done.length > 0 && (
        <>
          <div className="mb-[11px] text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">Done · {done.length}</div>
          <div className="flex flex-col gap-2.5">
            {done.map((t) => (
              <article key={t.id} className="flex items-center gap-3.5 rounded-[14px] border border-border bg-card px-4 py-3.5">
                <span className="flex h-[23px] w-[23px] flex-none items-center justify-center rounded-[7px] bg-brand">
                  <Check className="h-[13px] w-[13px] text-white" strokeWidth={2.6} />
                </span>
                <div className="min-w-0 flex-1">
                  <h4 className="text-[13.5px] font-semibold text-muted-foreground line-through">{t.title}</h4>
                  <div className="mt-0.5 text-[11px] font-medium text-muted-foreground">{t.propName} · completed</div>
                </div>
                <button type="button" onClick={() => resume(t.id)} className="flex-none bg-transparent text-[12px] font-semibold text-muted-foreground transition-colors hover:text-foreground">
                  Undo
                </button>
              </article>
            ))}
          </div>
        </>
      )}

    </div>
  )
}
